import { Injectable, OnDestroy } from '@angular/core';
import { ConfigService } from './config.service';
import { User, Config, CampusUnit, CampusStake, CampusWard, UnitType } from '../models';
import { BehaviorSubject, Subscription } from 'rxjs';
import * as Security from '../tokens/roles.tokens';
import { CampusWardsDataService } from './data/campus-wards-data.service';
import { ChurchUnitsDataService } from './data/church-units-data.service';

@Injectable()
export class UserService implements OnDestroy {
  private userSubject = new BehaviorSubject<User>(null);

  // we need to know if it's a ward or stake
  private proxyWardSubject = new BehaviorSubject<CampusWard>(null);
  private proxyStakeSubject = new BehaviorSubject<CampusStake>(null);

  // but other things just might want to know it as just a unit
  private proxyUnitSubject = new BehaviorSubject<CampusUnit>(null);

  private config: Config;
  private configSubscription: Subscription;

  user = this.userSubject.asObservable();
  proxyUnit = this.proxyUnitSubject.asObservable();
  proxyWard = this.proxyWardSubject.asObservable();
  proxyStake = this.proxyStakeSubject.asObservable();

  constructor(private configService: ConfigService,
    private wardService: CampusWardsDataService,
    private churchUnitsService: ChurchUnitsDataService) {

    this.configSubscription = this.configService.config$.subscribe(
      (config: Config) => {
        this.config = config;
        this.userSubject.next(config.user);

      // kind of a hack, for some reason the system uses a proxy ward even for a logged in user
      // so since we know the current user is a leader, let's set their proxy just like would have
      // happened at login
      if (config.unitType) {
        if (this.config.unitType === UnitType.Stake) {
          this.churchUnitsService
            .getCampusStakeDetails(this.config.campusUnitId)
            .subscribe(stake => {
              if (stake != null) {
                this.setProxyStake(stake);
              }
            });
        } else if (this.config.unitType === UnitType.Ward) {
          this.wardService
            .getCampusWardById(this.config.campusUnitId)
            .subscribe(w => {
              this.setProxyWard(w);
            });
        }
      }
      }
    );
  }

  ngOnDestroy() {
    this.configSubscription.unsubscribe();
  }

  unsubscribe() {
    this.ngOnDestroy();
  }
  /**
   * Whether or not a user is logged in.
   */
  isLoggedIn(): boolean {
    return this.configService.config.user != null;
  }

  /**
   * Tells you if the user has at least one of the given roles.
   */
  userHasRole(permissions: string[]): boolean {
    if (this.isLoggedIn() && this.configService.config.user.roles) {
      for (let j = 0; j < permissions.length; j++) {
        for (let i = 0; i < this.configService.config.user.roles.length; i++) {
          if (permissions[j] === this.configService.config.user.roles[i]) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Sets the campus ward the admin user wants to proxy for.
   */
  setProxyWard(ward: CampusWard) {
    if (
      this.userHasRole([Security.ApplicationRoles.byuiAdmin]) ||
      this.userHasRole([Security.ApplicationRoles.byuiLeader])
    ) {
      this.proxyWardSubject.next(ward);
      this.proxyUnitSubject.next(ward);
    }
  }

  /**
   * Sets the campus stake the admin user wants to proxy for.
   */
  setProxyStake(stake: CampusStake) {
    if (
      this.userHasRole([Security.ApplicationRoles.byuiAdmin]) ||
      this.userHasRole([Security.ApplicationRoles.byuiLeader])
    ) {
      this.proxyStakeSubject.next(stake);
      this.proxyUnitSubject.next(stake);
    }
  }

  /**
   * Gets the current value of the unit the user has proxied for.
   */
  getProxyUnit(): CampusUnit {
    return this.proxyUnitSubject.value;
  }

  getProxyWard(): CampusWard {
    return this.proxyWardSubject.value;
  }
}
